import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {cloneDeep, isEmpty, sum} from 'lodash';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import {IconoirWarningCircle} from '@ey-xd/motif-icon';
import {lightColor} from '@ey/styled-theme';

import {labels, currentResource} from '../../../../../util/utils';
import Utility from '../../../../../util/utilityfunctions';
import normalizeDashboardFilters from '../../../../../util/dashboard/normalizeDashboardFilters';
import {
	daySelection,
	toggleCCPEntityDetail,
	projectSessionKeys,
	dashboardTabId,
	dashboardChartColors
} from '../../../../../util/uiconstants';
import {loadStorage, saveStorage} from '../../../../Common/localStorage';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../actions/Metrics/dashboardFiltersAction';

import Icon from '@ey/canvascoreservices/Icon';

import Tile from '../../../../Common/Tile';
import Select from '../../../../CoreWrappers/Select';
import StyledFrameWrapper from './StyledFrameWrapper';
import Tooltip from '../../../../CoreWrappers/Tooltip';
import styled from 'styled-components';

const CCPDetail = ({engagementMetrics}) => {
	const [selectedDay, setSelectedDay] = useState(null);
	const [listDays, setListDays] = useState(null);
	const [selectedToggle, setSelectedToggle] = useState(toggleCCPEntityDetail.comingDue);
	const [hasValues, setHasValues] = useState(false);
	const [userChange, setUserChange] = useState(false);

	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);
	const dashboardContext = useSelector(({dashboardContext}) => dashboardContext);

	const dispatch = useDispatch();

	const getToggleColor = () => {
		switch (selectedToggle) {
			case toggleCCPEntityDetail.comingDue: {
				return dashboardChartColors.blue[2];
			}
			case toggleCCPEntityDetail.overdue: {
				return dashboardChartColors.red[2];
			}
			case toggleCCPEntityDetail.returned: {
				return dashboardChartColors.purple[2];
			}
		}
	};

	const [chartOptions, setChartOptions] = useState({
		chart: {
			type: 'bar',
			height: '600px',
			background: 'transparent'
		},
		title: {
			text: null
		},
		legend: {
			reversed: true
		},
		plotOptions: {
			bar: {maxPointWidth: 20},
			series: {
				stacking: 'normal',
				borderWidth: 2,
				borderColor: 'var(--neutrals-25)',
				point: {
					events: {
						click: (e) => {
							handlerOnGraphicClick && handlerOnGraphicClick(e);
						}
					}
				},
				click: (e) => {
					handlerOnGraphicClick && handlerOnGraphicClick(e);
				}
			}
		},
		credits: {
			enabled: false
		},
		tooltip: {
			useHTML: true,
			enabled: true,
			outside: true,
			followPointer: true,
			formatter: function(v) {
				const day = currentResource.toggleCCPEntityOptions.find((t) => {
					if (t.id === selectedToggle) {
						return t;
					}
				});
				return `<span class="name" style="font-size: 1.2rem; z-index: 100">${
					this.x
				}</span> | <span class="number-item">${Utility.getFormattedNumber(numberFormat, this.y)} ${day.label}</span>`;
			}
		},
		yAxis: {
			allowDecimals: false,
			title: {
				text: labels.requestCount
			},
			min: 0,
			max: 0,
			className: 'motif-body1-default-regular',
			labels: {
				useHTML: true,
				autoRotationLimit: 20,
				align: 'center',
				reserveSpace: true,
				allowOverlap: false,
				formatter: (e) => {
					const text = Utility.getFormattedNumber(numberFormat, e.value);
					return `<span class="motif-body2-default-light" title="${text}">${text}</span>`;
				}
			}
		},
		xAxis: {
			title: {
				text: null
			},
			labels: {
				useHTML: true,
				align: 'left',
				reserveSpace: true,
				allowOverlap: false,
				formatter: (e) => {
					const text = e.value;
					return `<span class="motif-body2-default-light" title="${text}">${text}</span>`;
				}
			}
		},
		series: [
			{
				data: [],
				showInLegend: false,
				color: getToggleColor()
			}
		]
	});

	const handlerOnGraphicClick = (highChartEvent) => {
		const highChartPoint = highChartEvent.point;

		const getToggleLabel = (toggle) => {
			switch (toggle) {
				case toggleCCPEntityDetail.comingDue: {
					return labels.comingDue;
				}
				case toggleCCPEntityDetail.overdue: {
					return labels.overdue?.charAt(0).toUpperCase() + labels.overdue?.slice(1);
				}
				case toggleCCPEntityDetail.returned: {
					return labels.returned;
				}
			}
		};
		const getFilteredEngagemets = (currentState) =>
			[...engagementMetrics].filter((metricsEng) => {
				if (
					dashboardContext?.entities
						?.find(({label}) => label === highChartPoint.category)
						?.engagements.includes(metricsEng.engId)
				) {
					const {ccptoggleId = null, ccpEntitydayId = null} = currentState;
					if (ccptoggleId === toggleCCPEntityDetail.comingDue) {
						if (ccpEntitydayId === daySelection.tomorrow && metricsEng.cRDueTom > 0) return true;
						if (ccpEntitydayId === daySelection.tomorrow && metricsEng.cRDue3 > 0) return true;
						if (ccpEntitydayId === daySelection.tomorrow && metricsEng.cRDue5 > 0) return true;
						if (ccpEntitydayId === daySelection.tomorrow && metricsEng.cRDue10 > 0) return true;
						if (ccpEntitydayId === daySelection.tomorrow && metricsEng.cRDue30 > 0) return true;
						return false;
					}
					if (ccptoggleId === toggleCCPEntityDetail.overdue && metricsEng.cROD > 0) return true;
					if (ccptoggleId === toggleCCPEntityDetail.returned && metricsEng.cRRetrn > 0) return true;
					return false;
				}
				return false;
			});

		const handlerOnClearFilter = () => {
			dispatch(clearFilterDashboardTiles(dashboardTabId.canvasClientPortal));
		};
		let currentState = loadStorage(psessionKEy);
		if (highChartPoint) {
			const dashboardFilters = normalizeDashboardFilters(
				[
					{
						serieLabel: highChartPoint.category,
						categoryLabel: getToggleLabel(currentState.ccptoggleId),
						onClearChip: handlerOnClearFilter
					}
				],
				getFilteredEngagemets(currentState),
				handlerOnClearFilter
			);
			dispatch(updateFilterDashboardTiles(dashboardTabId.canvasClientPortal, dashboardFilters));
		}
	};

	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.ccpdetailSession}`;

	useEffect(() => {
		const currentState = loadStorage(psessionKEy);
		if (currentState?.ccptoggleId) {
			setSelectedToggle(currentState.ccptoggleId);
		}
	}, []);

	useEffect(() => {
		calculateBars([]);
	}, [selectedDay, selectedToggle, dashboardContext, engagementMetrics]);

	useEffect(() => {
		areOtherValues();
	}, [chartOptions]);

	useEffect(() => {
		if (!userChange) {
			setDefaultDay();
		}
	}, [hasValues]);

	const setDefaultDay = () => {
		const days = [...Utility.getDaysOptions()];
		if (!isEmpty(days)) {
			let currentState = loadStorage(psessionKEy);
			if (currentState?.ccpEntitydayId) {
				setSelectedDay(days.find((d) => d.id == currentState.ccpEntitydayId));
			} else {
				setSelectedDay(days[2]);
			}
			setListDays(days);
		}
	};

	/**
	 * Update the option value passed to the chart.
	 * @param {Number} maxValue - Max value for y axis.
	 * @param {Array} categories - Name of the categories (rows) of the chart.
	 * @param {Array} data - Value of that category.
	 * */
	const updateBarValues = (maxValue, categories, data) => {
		const minHeight = 600;
		const minEntities = 15;
		const dynamicHight = 10;
		let partialOptions = {
			...chartOptions,
			tooltip: {
				useHTML: true,
				enabled: true,
				outside: true,
				followPointer: true,
				borderColor: getToggleColor(),
				formatter: function(v) {
					const day = currentResource.toggleCCPEntityOptions.find((t) => {
						if (t.id === selectedToggle) {
							return t;
						}
					});
					return `<span class="name" style="font-size: 1.2rem; z-index: 100">${
						this.x
					}</span> | <span style="text-transform: lowercase;">${Utility.getFormattedNumber(numberFormat, this.y)} ${
						day.label
					}</span>`;
				}
			},
			yAxis: {
				...chartOptions.yAxis,
				max: maxValue
			},
			xAxis: {
				...chartOptions.xAxis,
				categories: categories
			},
			series: [
				{
					data: data,
					showInLegend: false,
					color: getToggleColor()
				}
			]
		};

		if (categories.length > minEntities) {
			partialOptions.chart.scrollablePlotArea = {
				minHeight: minHeight * (categories.length / dynamicHight),
				opacity: 0
			};
		} else {
			partialOptions.chart = {
				type: 'bar',
				height: minHeight + 'px',
				opacity: 0
			};
		}

		setChartOptions(partialOptions);
	};

	const calculateComingDue = (metrics) => {
		let sum = 0;
		if (metrics?.length > 0) {
			for (let i = 0; i < metrics.length; i++) {
				const element = metrics[i];
				switch (selectedDay?.id) {
					case daySelection.tomorrow:
						sum += element.cRDueTom;
						break;
					case daySelection.next3days:
						sum += element.cRDue3;
						break;
					case daySelection.next5days:
						sum += element.cRDue5;
						break;
					case daySelection.next10days:
						sum += element.cRDue10;
						break;
					case daySelection.next30days:
						sum += element.cRDue30;
						break;
				}
			}
		}

		return sum;
	};

	const calculateOverdue = (metrics) => {
		return sum(metrics.map((item) => item.cROD));
	};

	const calculateReturned = (metrics) => {
		if (metrics?.length > 0) {
			return metrics.reduce((total, metric) => total + metric.cRRetrn, 0);
		}
		return 0;
	};

	const makeSum = (metrics) => {
		switch (selectedToggle) {
			case toggleCCPEntityDetail.comingDue:
				return calculateComingDue(metrics);
			case toggleCCPEntityDetail.overdue:
				return calculateOverdue(metrics);
			case toggleCCPEntityDetail.returned:
				return calculateReturned(metrics);
		}
	};

	const buildBars = (entities) => {
		//add 5% of the value to create space between the chart limit.
		const addMargin = 1.05;
		let maxValue = 0;
		let categories = [];
		let data = [];

		if (entities.length > 0) {
			for (let i = 0; i < entities.length; i++) {
				const element = entities[i];
				categories.push(element.label);
				let sum = makeSum(element.engagementMetrics);
				data.push(sum);
			}
			maxValue = addMargin * Math.max(...data);
		}

		updateBarValues(maxValue, categories, data);
	};

	const buildBarsList = (node, entities, barEntities) => {
		// check if entity is selected
		let entity = entities.find((x) => x.id === node.id && x.isEnabled);

		if (entity) {
			entity.engagementMetrics = cloneDeep(engagementMetrics.filter((x) => entity.engagements.includes(x.engId)));
			barEntities.push(entity);
		}

		// load children entities for each node
		node?.nodes?.forEach((n) => {
			buildBarsList(n, entities, barEntities);
		});

		return barEntities;
	};

	const calculateBars = (barEntities) => {
		let entities = null;
		dashboardContext.orgChart.forEach((node) => {
			entities = buildBarsList(cloneDeep(node), cloneDeep(dashboardContext.entities), barEntities);
		});

		if (entities) {
			buildBars([...entities]);
		} else {
			setHasValues(false);
		}
	};

	const areOtherValues = () => {
		switch (selectedToggle) {
			case toggleCCPEntityDetail.comingDue: {
				setHasValues(
					engagementMetrics?.some(
						(e) =>
							e.cRDueTom + e.cRDue3 + e.cRDue5 + e.cRDue10 + e.cRDue30 > 0 &&
							dashboardContext?.engagements?.includes(e.engId)
					) && chartOptions.yAxis.max > 0
				);
				break;
			}
			default: {
				setHasValues(chartOptions.yAxis.max > 0);
			}
		}
	};

	const showDropdown = () => {
		return (
			selectedToggle === toggleCCPEntityDetail.comingDue &&
			!isEmpty(listDays) &&
			engagementMetrics?.some(hasAnyComingDue)
		);
	};

	const hasAnyComingDue = (metric) =>
		metric.cRDueTom || metric.cRDue3 || metric.cRDue5 || metric.cRDue10 || metric.cRDue30;

	const getSubtitle = () => {
		return showDropdown() ? labels.emptyFrame : labels.emptyFrameShort;
	};

	const handlerOnDayChange = (dayId) => {
		setSelectedDay(listDays.find((d) => Number.parseInt(d.id) === Number.parseInt(dayId)));
		setUserChange(true);

		let currentState = loadStorage(psessionKEy);
		if (!currentState) {
			currentState = {};
		}
		currentState.ccpEntitydayId = Number.parseInt(dayId);
		saveStorage(psessionKEy, currentState);
	};

	const handlerOnSwitchChange = (toggleId) => {
		setSelectedToggle(Number.parseInt(toggleId));
		setUserChange(true);

		let currentState = loadStorage(psessionKEy);
		if (!currentState) {
			currentState = {};
		}
		currentState.ccptoggleId = Number.parseInt(toggleId);
		saveStorage(psessionKEy, currentState);
	};

	const renderStatusSelect = () => {
		const props = {
			value: selectedToggle,
			options: currentResource.toggleCCPEntityOptions,
			onChange: handlerOnSwitchChange,
			wrapperClass: 'card-header-select-size'
		};

		return <Select {...props} />;
	};

	const renderTimeSelect = () => {
		if (!listDays) return false;

		const props = {
			value: selectedDay?.id,
			options: listDays,
			onChange: handlerOnDayChange,
			wrapperClass: 'card-header-select-size'
		};

		return (
			<StyledFrameWrapper>
				{chartOptions.yAxis.max === 0 && (
					<Tooltip trigger={<Icon src={IconoirWarningCircle} />} tooltipText={labels.selectAnotherFrame} />
				)}
				<Select {...props} />
			</StyledFrameWrapper>
		);
	};

	const tileProps = {
		title: labels.requestsByEntity,
		subTitle: labels.basedOnTheSelectedCategory,
		header: renderStatusSelect(),
		subHeader: renderTimeSelect(),
		className: 'double-vertical',
		isEmpty: !hasValues,
		emptyTitle: labels.thereIsNoDataToDisplay,
		emptySubtitle: getSubtitle()
	};

	return (
		<Tile {...tileProps}>
			<StyledCCPDetail>
				<HighchartsReact highcharts={Highcharts} options={chartOptions} immutable />
			</StyledCCPDetail>
		</Tile>
	);
};

CCPDetail.defaultProps = {
	engagementMetrics: []
};

CCPDetail.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default CCPDetail;

const StyledCCPDetail = styled.div`
	align-self: center;
	width: var(--px-500);
`;
